import { useEffect, useRef, useState } from "react";
import { t } from "i18next";
import { CircleCheck } from "lucide-react";

type MicTestState = "testing" | "working" | "error";

const BAR_COUNT = 50;
const MAX_BAR_HEIGHT = 56;

const FreqEQ = ({ stream }: { stream: MediaStream }): JSX.Element => {
  const barRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    let raf = 0;
    const audioContext = new AudioContext();
    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.7;
    source.connect(analyser);

    const data = new Uint8Array(analyser.frequencyBinCount);
    const step = (): void => {
      analyser.getByteFrequencyData(data);
      for (let index = 0; index < BAR_COUNT; index++) {
        const bar = barRefs.current[index];
        if (!bar) continue;
        const bin = Math.floor((index / BAR_COUNT) * data.length);
        const value = (data[bin] ?? 0) / 255;
        bar.style.height = `${Math.max(3, value * MAX_BAR_HEIGHT)}px`;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);

    return (): void => {
      cancelAnimationFrame(raf);
      source.disconnect();
      void audioContext.close();
    };
  }, [stream]);

  return (
    <div className="flex h-full w-full items-center gap-[2px] px-[16px]">
      {Array.from({ length: BAR_COUNT }, (_, index) => (
        <div
          key={index}
          ref={(element) => {
            barRefs.current[index] = element;
          }}
          className="flex-1 rounded bg-[#0082EE]"
          style={{ height: 3 }}
        />
      ))}
    </div>
  );
};

export const OnboardingMicTest = (): JSX.Element => {
  const [state, setState] = useState<MicTestState>("testing");
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    let cancelled = false;
    void navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((track) => {
            track.stop();
          });
          return;
        }
        streamRef.current = stream;
        setState("working");
      })
      .catch(() => {
        if (!cancelled) setState("error");
      });

    return (): void => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((track) => {
        track.stop();
      });
    };
  }, []);

  return (
    <div className="space-y-3">
      <div className="flex h-[71px] items-center rounded-[10px] bg-[#E6F3FD]">
        {state === "working" && streamRef.current ? (
          <FreqEQ stream={streamRef.current} />
        ) : (
          <div className="flex w-full items-center gap-[2px] px-[16px]">
            {Array.from({ length: BAR_COUNT }, (_, index) => (
              <div
                key={index}
                style={{ height: MAX_BAR_HEIGHT * 0.5 }}
                className={`flex-1 rounded bg-[#0082EE] ${
                  state === "testing" ? "onboarding-eq-bar" : "opacity-40"
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {state === "working" && (
        <div className="flex h-[46px] items-center gap-2 rounded-[10px] bg-[#E6F6EF] px-4">
          <CircleCheck className="h-5 w-5 text-[#039757]" />
          <span className="text-sm font-semibold text-[#039757]">
            {t("onboarding.form.micWorking")}
          </span>
        </div>
      )}

      {state === "error" && (
        <div className="flex h-[46px] items-center gap-2 rounded-[10px] bg-[#FBEAEA] px-4">
          <span className="text-sm font-semibold text-[#C0392B]">
            {t("onboarding.form.micError")}
          </span>
        </div>
      )}
    </div>
  );
};
